/**
 * CSV in and out for the Flow configuration tabs (the owner's ask of
 * 2026-09-18), tolerant of what Excel does to a file on the way through:
 * a byte-order mark, a `sep=;` first line, CRLF, a semicolon or tab
 * delimiter from a European locale, smart quotes, trailing empty rows and
 * columns, and a leading apostrophe or `=` Excel adds to protect a value.
 * Every repair is named, so the import says what it changed.
 */
export interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
  fixes: string[];
}

export function parseCsv(input: string): ParsedCsv {
  const fixes: string[] = [];
  let text = input;
  if (text.charCodeAt(0) === 0xfeff) {
    text = text.slice(1);
    fixes.push("Removed the byte-order mark Excel writes at the start of a UTF-8 file.");
  }
  if (text.includes("\r")) {
    text = text.replace(/\r\n?/g, "\n");
    fixes.push("Converted Windows line endings.");
  }
  let delimiter = ",";
  const sep = /^sep=(.)\n/i.exec(text);
  if (sep) {
    delimiter = sep[1]!;
    text = text.slice(sep[0].length);
    fixes.push(`Honored Excel's "sep=${delimiter}" line and removed it.`);
  } else {
    const first = text.split("\n")[0] ?? "";
    const counts = { ",": (first.match(/,/g) ?? []).length, ";": (first.match(/;/g) ?? []).length, "\t": (first.match(/\t/g) ?? []).length };
    if (counts[";"] > counts[","] && counts[";"] >= counts["\t"]) {
      delimiter = ";";
      fixes.push("Read semicolons as the delimiter (a European Excel locale).");
    } else if (counts["\t"] > counts[","]) {
      delimiter = "\t";
      fixes.push("Read tabs as the delimiter.");
    }
  }
  const records = split(text, delimiter);
  // Smart quotes are straightened inside values, after splitting: a curly quote never delimits a field.
  if (records.some((r) => r.some((c) => /[“”‘’]/.test(c)))) {
    for (const r of records) for (let i = 0; i < r.length; i++) r[i] = r[i]!.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");
    fixes.push("Straightened smart quotes.");
  }
  while (records.length && records[records.length - 1]!.every((c) => c.trim() === "")) {
    records.pop();
    fixes.push("Dropped an empty row at the end.");
  }
  if (records.length === 0) return { headers: [], rows: [], fixes };

  let headers = records[0]!.map((h) => h.trim());
  while (headers.length && headers[headers.length - 1] === "") {
    headers.pop();
    if (!fixes.includes("Dropped empty columns at the right edge.")) fixes.push("Dropped empty columns at the right edge.");
  }
  headers = headers.map((h) => h.toLowerCase().replace(/\s+/g, "_"));

  let protectedValues = 0;
  const rows = records.slice(1).filter((cells) => cells.some((c) => c.trim() !== "")).map((cells) => {
    const row: Record<string, string> = {};
    headers.forEach((h, i) => {
      let v = (cells[i] ?? "").trim();
      // Excel protects a value that looks like a formula or number with a leading apostrophe or ="…" (the split marks the latter).
      if (v.startsWith(PROTECTED)) { v = v.slice(PROTECTED.length); protectedValues += 1; }
      else if (v.startsWith("'") && v.length > 1) { v = v.slice(1); protectedValues += 1; }
      row[h] = v;
    });
    return row;
  });
  if (protectedValues) fixes.push(`Unwrapped ${protectedValues} value${protectedValues === 1 ? "" : "s"} Excel had protected with an apostrophe or ="…".`);
  return { headers, rows, fixes };
}

/** Marks a field Excel wrote as ="…", so the value inside is taken and the wrapper counted as a repair. */
const PROTECTED = "\u0000=";

/** RFC 4180 splitting: quoted fields may hold the delimiter, newlines and doubled quotes. */
function split(text: string, delimiter: string): string[][] {
  const records: string[][] = [];
  let record: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else quoted = false;
      } else field += c;
    } else if (c === '"') {
      if (field === "=") field = PROTECTED;
      quoted = true;
    } else if (c === delimiter) {
      record.push(field); field = "";
    } else if (c === "\n") {
      record.push(field); records.push(record); record = []; field = "";
    } else field += c;
  }
  if (field !== "" || record.length) { record.push(field); records.push(record); }
  return records;
}

export function toCsv(headers: string[], rows: Array<Record<string, string | number | boolean | undefined>>): string {
  const cell = (v: unknown) => {
    const s = v === undefined || v === null ? "" : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.map(cell).join(","), ...rows.map((r) => headers.map((h) => cell(r[h])).join(","))].join("\r\n") + "\r\n";
}

export function downloadText(filename: string, text: string, type = "text/csv"): void {
  const blob = new Blob([text], { type: `${type};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** A File's text, by the modern method where the browser has it and by FileReader where it does not. */
export function readFileText(file: File): Promise<string> {
  if (typeof file.text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the file"));
    reader.readAsText(file);
  });
}
